import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy01, Key01, RefreshCw01, Trash01 } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { TokenPicker } from "@/components/app/TokenPicker";
import { usePrivatTokenAction, usePrivatTokenStatus, type PrivatLoginResult } from "@/lib/apiTools";
import { formatDateTime } from "@/lib/format";
import { Field, inputCls } from "../shared";

/**
 * Stav Bearer tokenu pro Privat Mobile API — a jeho získání.
 *
 * Bez access tokenu neprojde **žádné** volání na jejich API, ale server si ho obstarával
 * sám a neviditelně: nebylo poznat, jestli nějaký drží, jak dlouho ještě platí, ani proč
 * se ho nepodařilo získat. Panel je proto nahoře nad operacemi, ne pod nimi.
 *
 * Stav se čte endpointem, který **nepřihlašuje** — jinak by pouhé otevření stránky bušilo
 * do auth serveru.
 */
export function PrivatTokenPanel() {
  const { t } = useTranslation();
  const [tokenId, setTokenId] = useState("");
  const [outcome, setOutcome] = useState<PrivatLoginResult | null>(null);
  const [copied, setCopied] = useState(false);

  const trimmed = tokenId.trim();
  const { data: status } = usePrivatTokenStatus(trimmed, trimmed.length > 0);
  const action = usePrivatTokenAction(trimmed);

  const run = (kind: "login" | "force" | "invalidate") => {
    setCopied(false);
    action.mutate(kind, { onSuccess: (r) => setOutcome(kind === "invalidate" ? null : r) });
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Bez zabezpečeného kontextu schránka není; pole zůstává vybíratelné ručně.
      setCopied(false);
    }
  };

  const busy = action.isPending;
  const hasToken = status?.hasToken === true;

  return (
    <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <div className="flex flex-wrap items-center gap-2">
        <Key01 className="size-4 text-tertiary" />
        <h2 className="flex-1 text-md font-semibold text-primary">{t("apitools.privatToken.title")}</h2>
        {status && (
          <Badge type="pill-color" size="sm" color={hasToken ? "success" : "gray"}>
            {hasToken ? t("apitools.privatToken.held") : t("apitools.privatToken.none")}
          </Badge>
        )}
        {status && !status.autoLogin && (
          <Badge type="pill-color" size="sm" color="warning">
            {t("apitools.privatToken.autoLoginOff")}
          </Badge>
        )}
      </div>

      <p className="text-xs text-tertiary">{t("apitools.privatToken.hint")}</p>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-64 flex-1">
          <Field label={t("apitools.f.tokenId")}>
            <TokenPicker
              value={tokenId}
              onChange={(v) => {
                setTokenId(v);
                setOutcome(null);
              }}
              authServerType="PRIVAT"
              activeOnly
              placeholder={t("apitools.selectToken")}
            />
          </Field>
        </div>
        <div className="flex gap-2">
          <Button size="sm" iconLeading={Key01} isDisabled={!trimmed || busy} onPress={() => run("login")}>
            {t("apitools.privatToken.obtain")}
          </Button>
          <Button
            size="sm"
            color="secondary"
            iconLeading={RefreshCw01}
            isDisabled={!trimmed || busy}
            onPress={() => run("force")}
          >
            {t("apitools.privatToken.refresh")}
          </Button>
          <Button
            size="sm"
            color="secondary"
            iconLeading={Trash01}
            isDisabled={!trimmed || busy || !hasToken}
            onPress={() => run("invalidate")}
          >
            {t("apitools.privatToken.discard")}
          </Button>
        </div>
      </div>

      {status && hasToken && (
        <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
          <div className="flex gap-2">
            <dt className="text-tertiary">{t("apitools.privatToken.obtainedAt")}:</dt>
            <dd className="text-secondary">{formatDateTime(status.obtainedAt, { seconds: true })}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-tertiary">{t("apitools.privatToken.validUntil")}:</dt>
            <dd className="text-secondary">
              {formatDateTime(status.expiresAt, { seconds: true })}{" "}
              <span className="text-tertiary">
                ({t("apitools.privatToken.remaining", { seconds: status.ttlSeconds })})
              </span>
            </dd>
          </div>
          {status.source && (
            <div className="flex gap-2">
              <dt className="text-tertiary">{t("apitools.privatToken.source")}:</dt>
              <dd className="text-secondary">{status.source}</dd>
            </div>
          )}
        </dl>
      )}

      {/* Platnost je náš odhad — jejich API ji nevrací. Tvrdit ji bez téhle věty by z odhadu
          udělalo zdánlivou jistotu a operátor by hledal chybu jinde, než kde je. */}
      {status?.expiryEstimated && hasToken && (
        <p className="text-xs text-tertiary">{t("apitools.privatToken.estimated")}</p>
      )}

      {status && hasToken && status.accessToken && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-secondary">accessToken</span>
            <Button size="sm" color="link-gray" iconLeading={Copy01} onPress={() => void copy(status.accessToken!)}>
              {copied ? t("asymtools.copied") : t("asymtools.copy")}
            </Button>
          </div>
          <textarea
            readOnly
            rows={2}
            aria-label="accessToken"
            className={`${inputCls} font-mono text-xs`}
            value={status.accessToken}
          />
        </div>
      )}

      {outcome && !outcome.success && (
        <div role="alert" className="flex flex-col gap-2 rounded-md bg-secondary p-3 text-xs ring-1 ring-error">
          <p className="font-semibold text-error-primary">
            {t("apitools.privatToken.failed", { step: outcome.failedStep ?? "?" })}
          </p>
          {outcome.error && <p className="text-secondary">{outcome.error}</p>}
          {outcome.steps?.map((s, i) => (
            <p key={i} className="font-mono break-all text-tertiary">
              {s.ok ? "✓" : "✗"} {s.step} · {s.call}
              {s.status != null && ` · HTTP ${s.status}`}
              {s.detail && ` · ${s.detail}`}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
